import { getClassLevel } from './classIdentity.mjs';

import { FIGHTING_STYLES, getClassFightingStyle, hasFightingStyle, canChangeClassFightingStyle } from './fightingStyle.mjs';

// Fighting Style rules are shared with Ranger in fightingStyle.mjs; these keep the Fighter-specific API.
export { FIGHTING_STYLES, hasFightingStyle };
export const getFighterLevel = character => getClassLevel(character, 'fighter');
export const getFightingStyle = character => getClassFightingStyle(character, 'fighter');
export const canChangeFightingStyle = character => canChangeClassFightingStyle(character, 'fighter');
export const fighterMasteryCount = level => level < 1 ? 0 : level < 4 ? 3 : level < 10 ? 4 : level < 16 ? 5 : 6;
const category = weapon => String(weapon?.category || weapon?.weaponCategory || weapon?.weaponType || weapon?.type || '').toLowerCase();
const property = (weapon, name) => (weapon?.properties || []).some(value =>
  String(value?.name || value).toLowerCase().replace(/\s*\(.*/, '').trim() === name);
export const archeryAttackBonus = (character, weapon) => hasFightingStyle(character, 'archery') && category(weapon).includes('ranged') ? 2 : 0;
export const greatWeaponFightingApplies = (character, attack = {}) => hasFightingStyle(character, 'great-weapon-fighting') &&
  attack.kind === 'weapon' && category(attack.weapon).includes('melee') && attack.isThrown !== true &&
  (property(attack.weapon, 'two-handed') || (property(attack.weapon, 'versatile') && attack.hands === 'two-handed'));
export const fightingStyleDamageDie = (value, enabled) => enabled && (value === 1 || value === 2) ? 3 : value;

export const getSecondWindProfile = character => {
  const level = getFighterLevel(character);
  const maximum = level < 1 ? 0 : level < 4 ? 2 : level < 10 ? 3 : 4;
  const spent = Math.max(0, Math.floor(Number(character?.fighterResources?.secondWindSpent) || 0));
  return { level, maximum, spent, remaining: Math.max(0, maximum - spent) };
};
export const secondWindRestState = (character, type) => ({
  ...character.fighterResources,
  actionSurgeSpent: 0,
  secondWindSpent: type === 'long' ? 0 : Math.max(0, getSecondWindProfile(character).spent - 1),
  ...(type === 'long' ? { indomitableSpent: 0 } : {}),
  revision: (Number(character.fighterResources?.revision) || 0) + 1,
});
/** Indomitable (Fighter 9): reroll a failed save with the Fighter level added. 1 use, 2 at 13, 3 at 17; Long Rest. */
export const indomitableUses = level => (level < 9 ? 0 : level < 13 ? 1 : level < 17 ? 2 : 3);
export const getIndomitableProfile = character => {
  const level = getFighterLevel(character);
  const maximum = indomitableUses(level);
  const spent = Math.max(0, Math.floor(Number(character?.fighterResources?.indomitableSpent) || 0));
  return { level, maximum, spent, remaining: Math.max(0, maximum - spent) };
};
export const applyFighterResourceUpdate = (character, fighterResources, changes = {}) =>
  Number(character?.fighterResources?.revision || 0) > Number(fighterResources?.revision || 0)
    ? character : { ...character, ...changes, fighterResources };

export const getActionSurgeProfile = character => {
  const level = getFighterLevel(character);
  const maximum = level < 2 ? 0 : level < 17 ? 1 : 2;
  const spent = Math.max(0, Math.floor(Number(character?.fighterResources?.actionSurgeSpent) || 0));
  return { level, maximum, spent, remaining: Math.max(0, maximum - spent) };
};
/** Studied Attacks (Fighter 13): after missing a creature with an attack roll, the next attack roll against it before the
 * end of the Fighter's next turn has Advantage. The mark is an effect from the Fighter on that creature. */
export const STUDIED_ATTACKS_DEFINITION_ID = 'fighter:studied-attacks';
export const STUDIED_ATTACKS_ADVANTAGE_SOURCE = 'Fighter: Studied Attacks';
export const hasStudiedAttacks = character => getFighterLevel(character) >= 13;
export const getStudiedAttacksEffect = (combat, attackerId, targetId) => (combat?.activeEffects || []).find(effect =>
  effect?.definitionId === STUDIED_ATTACKS_DEFINITION_ID && String(effect.sourceCombatantId) === String(attackerId)
  && String(effect.targetCombatantId) === String(targetId)) || null;

/** Tactical Master (Fighter 9): an attack with a weapon whose mastery the Fighter can use may use Push, Sap or Slow
 * instead of that weapon's own mastery property, for that attack. */
export const TACTICAL_MASTER_PROPERTIES = Object.freeze(['push', 'sap', 'slow']);
export const hasTacticalMaster = character => getFighterLevel(character) >= 9;
/** The property this attack uses: a valid Tactical Master choice, else null (the weapon's own). */
export const tacticalMasteryChoice = (character, canUseWeaponMastery, choice) =>
  canUseWeaponMastery && hasTacticalMaster(character) && TACTICAL_MASTER_PROPERTIES.includes(choice) ? choice : null;

export const hasNonMagicAction = ledger => !(ledger?.actionSpent ?? Boolean(ledger?.attackAction?.id)) || Boolean(ledger?.actionSurge && !ledger.actionSurge.spent);
